import React, {FC, useEffect, useState} from "react";
import Container from "@material-ui/core/Container";
import {
    Box, Breadcrumbs,
    Card, CircularProgress,
    Grid, makeStyles, Typography, useMediaQuery, useTheme,
} from "@material-ui/core";
import Navigation from "../../../Components/Navigation";
import {colorInputBorderGrayish} from "../../../Constants/theme";
import RecordListItem from "../../../Components/ListItems/RecordListItem/RecordListItem";
import API from "../../../helpers/apiHelpers";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../../redux/store";
import {MedicalRecordsModal} from "../../../Components";
import moment from "moment";
import {getAppointmentType} from "../../../Constants/serviceType";
import {getNotesForPet} from "../../../redux/pets/pets.actions";
import {Link} from "react-router-dom";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";

interface Props {
	isOnBoarding: boolean,
	setIsOnBoarding: React.Dispatch<React.SetStateAction<boolean>>,
}

const useStyle = makeStyles(() => ({
    itemDivider: {
        borderBottom: `1px solid ${colorInputBorderGrayish}`
    },
    button: {
        padding: "12px 44px"
    }
}));

const MedicalRecords: FC = () => {
    const [recordsList, setRecordsList] = useState(null);
    const [loaderShowed, setLoaderShowed] = useState(false);
    const [openNotesModal, setOpenNotesModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [vetIds, setVetIds] = useState(null);
    const [vetProfiles, setVetProfiles] = useState(null);
    const pet = useSelector((state: RootState) => state?.pets.selectedPet);
    const classes = useStyle();
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        (async () => {
            try {
                setLoaderShowed(true);
                const records = await dispatch(getNotesForPet(pet.id));
                setRecordsList(records);
                const vetIds = records.map(({appointment}) => appointment.vetUserId);
                const setVets = new Set(vetIds);
                const vetArrayResult = Array.from(setVets);
                setVetIds(vetArrayResult);
            } catch (err) {
                //
            } finally {
                setLoaderShowed(false);
            }
        })();
    }, []);

    useEffect(() => {
        if (vetIds?.length) {
            setLoaderShowed(true);
            const vetProfilePromises = vetIds.map(item => {
                if (!item) {
                    return "";
                } else {
                    return API.getProfiles({userId: item});
                }
            });
            Promise.all(vetProfilePromises)
                .then(values => {
                    let vetProfileArray = [];
                    values.forEach((item: object[]) => vetProfileArray = [...vetProfileArray, ...item]);
                    setVetProfiles(vetProfileArray);
                    setLoaderShowed(false);
                })
                .catch(console.error)
                .finally(() => {
                    setLoaderShowed(false);
                });
        }
    }, [vetIds]);

    return (
        <Container maxWidth="md">
            <Box marginTop="115px" marginBottom="75px">
                <Grid container>
                    {!isMobile &&
						<Grid item md={3} xs={12}>
						    <Navigation />
						</Grid>
                    }
                    <Grid item md={9} sm={12} xs={12}>
                        <Card
                            variant="outlined"
                            style={{padding: 25}}
                        >   
                        <Box marginBottom="55px">
                            <Breadcrumbs separator={<NavigateNextIcon fontSize="large"/>} aria-label="breadcrumb">
                                <Link color="inherit" to={`/pets/${pet.id}`}  style={{textDecoration: "none"}}>
                                    <Typography variant="h2">{pet.name}</Typography>
                                </Link>
                                <Typography variant="h2" style={{wordBreak: "break-word"}}>Medical Records</Typography>
                            </Breadcrumbs>
                        </Box>
                            {	loaderShowed ?
                                <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                                    <CircularProgress/>
                                </Box>
                                : (recordsList?.length && vetProfiles?.length) ?
                                    recordsList
                                        .sort((a, b) => moment(b.created).diff(moment(a.created)))
                                        .map(({id, created, appointment}, idx) => {
                                            const vet = vetProfiles.find(profile => profile.userId === appointment.vetUserId);
                                            const title = appointment.status === "NOT_APPLICABLE" ? "In-Clinic Appointment" : `${getAppointmentType(appointment.type)} - Dr. ${vet.lastName}`;
                                            const noteCreated = moment(created).format("MMMM DD, YYYY");
                                            return <RecordListItem
                                                key={idx}
                                                id={id}
                                                title={title}
                                                date={noteCreated}
                                                setSelectedRecord={setSelectedRecord}
                                                setOpenModal={setOpenNotesModal}
                                                styles={(idx === recordsList.length - 1) ? "" : classes.itemDivider}
                                            />;
                                        })
                                    :
                                    <Typography variant="h6">No medical records</Typography>
                            }
                            <MedicalRecordsModal isOpen={openNotesModal} setOpenDialog={setOpenNotesModal} noteId={selectedRecord}/>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export default MedicalRecords;
